/** Maps file extension → canonical MIME type. Single source of truth used by
 *  useFetchDataRoomFiles (MIME resolution) and DocumentViewer (render routing). */
export const EXT_MIME: Record<string, string> = {
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv',
    // Images — needed for extension-based fallback when server returns application/octet-stream
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
};

/** All MIME types that open in the preview drawer instead of downloading. */
export const PREVIEWABLE_MIME_TYPES = ['application/pdf', 'application/csv', ...Object.values(EXT_MIME)];

/** MIME types routed to SpreadsheetViewer. */
export const SPREADSHEET_TYPES = [EXT_MIME.xlsx, EXT_MIME.xls, EXT_MIME.csv, 'application/csv'];

/** MIME types treated as CSV (parsed via Papa, not XLSX). EXT_MIME.csv === 'text/csv'. */
export const CSV_MIME_TYPES = [EXT_MIME.csv, 'application/csv'];

/** Bound the in-app spreadsheet preview so a large/wide sheet cannot build an
 *  unbounded array (defval:'' fills the whole used range) or DOM, which crashed
 *  the renderer on ~7 MB sparse-but-wide stock statements (RCPI-10375). */
export const MAX_PREVIEW_ROWS = 1000;
export const MAX_PREVIEW_COLS = 50;

/** Arrow key identifiers used for keyboard navigation in SpreadsheetViewer. */
export const ARROW_UP = 'ArrowUp';
export const ARROW_DOWN = 'ArrowDown';
export const ARROW_LEFT = 'ArrowLeft';
export const ARROW_RIGHT = 'ArrowRight';
export const ARROW_KEYS = [ARROW_UP, ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT] as const;

/** Shared state shape for components that preview blobs in FileViewerDrawer. */
export interface BlobViewerState {
    url: string;
    blob: Blob;
}

/** Create a blob URL for preview, revoking any previous one. */
export function openBlobViewer(blob: Blob, previousUrl?: string | null): BlobViewerState {
    if (previousUrl) URL.revokeObjectURL(previousUrl);
    return { url: URL.createObjectURL(blob), blob };
}

/** Revoke the blob URL and return null (for clearing state). */
export function closeBlobViewer(state: BlobViewerState | null): null {
    if (state?.url) URL.revokeObjectURL(state.url);
    return null;
}
